import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Linkedin, TrendingUp, TrendingDown, History } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { PageHeader, StatCard, Table, StatusBadge } from "@/admin/ui";

const ACTION_COLORS: Record<string, string> = {
  approved: "#4ADE80", published: "#4ADE80", created: "#00E5FF", updated: "#00E5FF",
  deleted: "#F87171", dismissed: "#F87171", rejected: "#F87171",
};

const ActivityFeed = () => {
  const { data: perms } = useQuery({
    queryKey: ["my-permissions"],
    queryFn: async () => (await api.get("/admin/me/permissions")).data,
  });
  const isSuper = perms?.permissions?.includes("*");
  const { data } = useQuery({
    queryKey: ["dash-activity"],
    queryFn: async () => (await api.get("/admin/audit-logs?page=1")).data,
    refetchInterval: 60_000,
    enabled: !!isSuper,
  });
  if (!isSuper || !data) return null;
  const items = (data.items || []).slice(0, 8);
  return (
    <div className="mb-8 bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="team-activity-feed">
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 font-heading font-bold text-white text-base">
          <History className="h-4 w-4 text-[#00E5FF]" /> Team activity
        </h2>
        <Link to="/admin/audit-logs" data-testid="activity-view-all"
          className="font-mono text-[10px] uppercase tracking-wider text-[#00E5FF] hover:underline">
          Full audit trail →
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm" data-testid="activity-empty">No admin actions yet.</p>
      ) : (
        <ul className="divide-y divide-white/5">
          {items.map((a: any) => {
            const verb = String(a.action || "").replace(/_/g, " ");
            const color = Object.entries(ACTION_COLORS).find(([k]) => verb.includes(k))?.[1] || "#9CA3AF";
            return (
              <li key={a.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-2"
                data-testid={`activity-row-${a.id}`}>
                <span className="h-1.5 w-1.5 rounded-full shrink-0 self-center" style={{ backgroundColor: color }} />
                <span className="text-white text-xs font-medium">{a.actor_email}</span>
                <span className="text-gray-400 text-xs">{verb}</span>
                {a.target_type && <span className="font-mono text-[10px] text-gray-600">{a.target_type}</span>}
                {a.meta?.email && <span className="font-mono text-[10px] text-gray-500">{a.meta.email}</span>}
                <span className="ml-auto font-mono text-[10px] text-gray-600 whitespace-nowrap">{timeAgo(a.created_at)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const Sparkline = ({ points }: { points: { date: string; count: number }[] }) => {
  const w = 160, h = 40;
  const counts = points.map((p) => p.count);
  const min = Math.min(...counts), max = Math.max(...counts);
  const span = max - min || 1;
  const step = w / (counts.length - 1);
  const y = (c: number) => h - 5 - ((c - min) / span) * (h - 10);
  const path = counts.map((c, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${y(c).toFixed(1)}`).join(" ");
  const up = counts[counts.length - 1] >= counts[0];
  const color = up ? "#4ADE80" : "#F87171";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} data-testid="linkedin-follower-sparkline"
      className="shrink-0" aria-label="30-day follower trend">
      <path d={`${path} L${w},${h} L0,${h} Z`} fill={color} opacity="0.08" stroke="none" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={w} cy={y(counts[counts.length - 1])} r="2.5" fill={color} />
    </svg>
  );
};

const LinkedInFollowersCard = () => {
  const { data: li } = useQuery({
    queryKey: ["linkedin-followers"],
    queryFn: async () => (await api.get("/admin/social/linkedin/followers")).data,
    refetchInterval: 300_000,
  });
  if (!li) return null;
  return (
    <a href={li.company_url || "https://www.linkedin.com/company/newslayer"} target="_blank" rel="noreferrer"
      data-testid="linkedin-followers-card"
      className="flex flex-wrap items-center justify-between gap-3 bg-[#121212] border border-[#0A66C2]/30 rounded-xl px-5 py-4 mb-8 hover:border-[#0A66C2]/60 transition-colors">
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-lg bg-[#0A66C2]/15 border border-[#0A66C2]/40 flex items-center justify-center shrink-0">
          <Linkedin className="h-4 w-4 text-[#38BDF8]" />
        </span>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">LinkedIn Page Followers</p>
          {li.count != null ? (
            <p className="font-mono text-2xl font-bold text-white leading-tight" data-testid="linkedin-follower-count">
              {li.count.toLocaleString()}
            </p>
          ) : (
            <p className="text-sm text-gray-400" data-testid="linkedin-followers-empty">
              {li.connected ? "Fetching…" : "Connect LinkedIn in Social to start tracking"}
            </p>
          )}
        </div>
      </div>
      {(li.history || []).length >= 2 && <Sparkline points={li.history} />}
      {li.weekly_delta != null ? (
        <span data-testid="linkedin-weekly-growth"
          className={`inline-flex items-center gap-1.5 font-mono text-sm font-bold rounded-lg border px-3 py-1.5 ${
            li.weekly_delta >= 0
              ? "text-[#4ADE80] bg-[#4ADE80]/10 border-[#4ADE80]/30"
              : "text-red-400 bg-red-500/10 border-red-500/30"}`}>
          {li.weekly_delta >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          {li.weekly_delta >= 0 ? "+" : ""}{li.weekly_delta.toLocaleString()} this week
        </span>
      ) : li.connected ? (
        <span className="font-mono text-[10px] uppercase tracking-wider text-gray-500">
          weekly growth shows after 2+ days of tracking
        </span>
      ) : null}
    </a>
  );
};

const KIND_LABELS: Record<string, string> = {
  image: "AI Thumbnails", text: "Editorial AI", translation: "Translations", tts: "Audio Briefs",
};

const AiBudgetMeter = () => {
  const { data } = useQuery({
    queryKey: ["admin-ai-usage"],
    queryFn: async () => (await api.get("/admin/ai/usage")).data,
    refetchInterval: 60000,
  });
  if (!data) return null;
  const paused = data.guard?.paused;
  const maxCost = Math.max(0.01, ...(data.today.by_kind || []).map((k: any) => k.cost_est));
  return (
    <section className="mb-8 rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="ai-budget-meter">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-heading font-semibold text-white">AI Budget Meter</h2>
        <Link to="/admin/intelligence" data-testid="ai-budget-drilldown"
          className="font-mono text-[11px] uppercase text-[#A78BFA] hover:text-white transition-colors">
          Full breakdown & controls →
        </Link>
        <span data-testid="ai-guard-status"
          className={`font-mono text-[11px] font-bold uppercase tracking-wider rounded px-2 py-1 border ${
            paused ? "text-amber-400 border-amber-400/40 bg-amber-400/10" : "text-green-400 border-green-400/40 bg-green-400/10"}`}>
          {paused ? `Paused until ${new Date(data.guard.paused_until).toLocaleTimeString()} (daily cap)` : "AI Active"}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">Today's est. spend</p>
          <p className="font-mono text-3xl font-bold text-white mt-1" data-testid="ai-spend-today">
            ${data.today.cost_est.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {data.today.calls} AI calls · yesterday ${data.yesterday.cost_est.toFixed(2)} ({data.yesterday.calls} calls)
          </p>
        </div>
        <div className="space-y-2">
          {(data.today.by_kind || []).map((k: any) => (
            <div key={k.kind} className="flex items-center gap-3">
              <span className="w-28 text-xs text-gray-400 shrink-0">{KIND_LABELS[k.kind] || k.kind}</span>
              <div className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                <div className={`h-full rounded-full ${k.kind === "image" ? "bg-[#7C3AED]" : "bg-[#00E5FF]"}`}
                  style={{ width: `${Math.max(4, (k.cost_est / maxCost) * 100)}%` }} />
              </div>
              <span className="font-mono text-xs text-gray-400 w-24 text-right">
                {k.calls} · ${k.cost_est.toFixed(2)}
              </span>
            </div>
          ))}
          {(data.today.by_kind || []).length === 0 && (
            <p className="text-sm text-gray-500">No AI usage recorded yet today.</p>
          )}
        </div>
      </div>
      <p className="text-[11px] text-gray-600 mt-4">
        Costs are estimates. The Universal Key's daily cap resets at midnight UTC — raise it in your Emergent
        profile (Profile → Universal Key). AI thumbnails now only generate when a story has no source photo.
      </p>
    </section>
  );
};

const TopSharedCard = () => {
  const { data } = useQuery({
    queryKey: ["admin-top-shared"],
    queryFn: async () => (await api.get("/admin/top-shared")).data,
    staleTime: 60 * 1000,
  });
  const rows = data?.rows || [];
  return (
    <section className="mb-8 rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="top-shared-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-semibold text-white">Stories that travel</h2>
        <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">X share taps</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-gray-500 text-sm" data-testid="top-shared-empty">
          No share taps yet — counts appear as readers share stories on X from the applause prompt.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((r: any, i: number) => (
            <div key={r.slug} className="flex items-center gap-3 text-sm" data-testid={`top-shared-row-${r.slug}`}>
              <span className="font-mono text-xs text-gray-600 w-5">{i + 1}.</span>
              <a href={`/article/${r.slug}`} target="_blank" rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate text-gray-300 hover:text-[#00E5FF] transition-colors">{r.title}</a>
              <span className="font-mono text-xs text-gray-500">{r.week} this week</span>
              <span className="font-mono text-xs font-bold text-[#00E5FF]">{r.total} total</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
    refetchInterval: 30000,
  });

  if (isLoading || !data) return <div className="text-gray-500 py-10">Loading dashboard...</div>;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Operations Dashboard" sub="Ingestion health, growth and content at a glance" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-8">
        <StatCard label="Published" value={data.articles.published} to="/admin/articles?status=published" />
        <StatCard label="Pending Review" value={data.articles.pending} accent="#F59E0B" to="/admin/articles?status=imported_pending_review" />
        <StatCard label="Review Queue" value={data.review_open} accent="#F59E0B" to="/admin/review-queue" />
        <StatCard label="Active Sources" value={data.sources_active} accent="#7C3AED" to="/admin/sources" />
        <StatCard label="Leads Total" value={data.leads.total} accent="#FFD600" sub={`+${data.leads.week} this week`} to="/admin/leads" />
        <StatCard label="Subscribers" value={data.leads.subscribers} accent="#10B981" to="/admin/leads" />
        <StatCard label="Users" value={data.users_total} accent="#00E5FF" to="/admin/users" />
      </div>

      <LinkedInFollowersCard />

      <ActivityFeed />

      <AiBudgetMeter />

      <TopSharedCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold text-white">Recent Ingestion Runs</h2>
            <Link to="/admin/runs" className="text-xs text-[#00E5FF] hover:underline">All runs</Link>
          </div>
          <Table headers={["Source", "Status", "New", "Dupes", "When"]}>
            {(data.recent_runs || []).map((r: any) => (
              <tr key={r.id}>
                <td className="px-4 py-2.5 text-white">{r.source_name}</td>
                <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2.5 font-mono text-green-400">{r.created_count}</td>
                <td className="px-4 py-2.5 font-mono text-gray-500">{r.duplicate_count}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{timeAgo(r.started_at)}</td>
              </tr>
            ))}
          </Table>
          {data.failed_runs_week > 0 && (
            <p className="text-red-400 text-xs mt-2 font-mono">⚠ {data.failed_runs_week} failed run(s) in the last 7 days</p>
          )}
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold text-white">Top Performing Stories</h2>
            <Link to="/admin/analytics" className="text-xs text-[#00E5FF] hover:underline">Analytics</Link>
          </div>
          <Table headers={["Title", "Views", "Source"]}>
            {(data.top_articles || []).map((a: any) => (
              <tr key={a.id}>
                <td className="px-4 py-2.5 text-white max-w-[280px] truncate">
                  <Link to={`/article/${a.slug}`} className="hover:text-[#00E5FF]">{a.title}</Link>
                </td>
                <td className="px-4 py-2.5 font-mono text-[#00E5FF]">{a.views}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{a.source_name}</td>
              </tr>
            ))}
          </Table>
        </section>
      </div>

      <section className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-white">Latest Imports</h2>
          <Link to="/admin/articles" className="text-xs text-[#00E5FF] hover:underline">Manage articles</Link>
        </div>
        <Table headers={["Title", "Source", "Status", "Confidence", "When"]}>
          {(data.latest_imports || []).map((a: any) => (
            <tr key={a.id}>
              <td className="px-4 py-2.5 text-white max-w-[360px] truncate">{a.title}</td>
              <td className="px-4 py-2.5 text-gray-400 text-xs">{a.source_name}</td>
              <td className="px-4 py-2.5"><StatusBadge status={a.status} /></td>
              <td className="px-4 py-2.5 font-mono text-xs text-gray-400">
                {a.import_confidence_score != null ? `${Math.round(a.import_confidence_score * 100)}%` : "—"}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{timeAgo(a.created_at)}</td>
            </tr>
          ))}
        </Table>
      </section>
    </div>
  );
}
